import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useApplicationContext } from 'shared/common/utils/applicationContext';

import { mortgageMiniCalculatorApi } from '../api';
import { closeModal, openSubmittedAnketaModal, openUnfinishedAnketaModal, setShortFormRedirectUrl } from '../redux';
import { selectIsCianAuthorized, selectIsOffersFetching, selectSaveOffersParams } from '../selectors/calculator';
import { buildShortFormUrl, redirect, resolveActualApplication, withRequestTimeout } from '../utils';
import { IOfferForAnalytic } from '../utils/tracking/types';

export interface IUseShortFormAuthActionOptions {
  trackingFunction?: () => void;
  offerForAnalytic?: IOfferForAnalytic | null;
  listNumber?: number | null;
  bankId?: number | null;
}

export interface IUseShortFormAuthActionReturn {
  isLoading: boolean;
  handleShortFormAuthAction: () => Promise<void>;
}

export const useShortFormAuthAction = (options: IUseShortFormAuthActionOptions = {}): IUseShortFormAuthActionReturn => {
  const { trackingFunction, offerForAnalytic, listNumber, bankId } = options;
  const { logger } = useApplicationContext();
  const dispatch = useDispatch();
  const isCianAuthorized = useSelector(selectIsCianAuthorized);
  const saveOffersParams = useSelector(selectSaveOffersParams);
  const isOffersFetching = useSelector(selectIsOffersFetching);

  /* Ответы приходят не сразу, за это время человек успевает закрыть модалку оффера и уйти
     листать выдачу: показывать ему модалку черновика задним числом нельзя. Редирект наоборот
     доводим до конца — переход в анкету он и просил кликом, а другого пути к ней у него нет */
  const isMounted = useRef(true);

  useEffect(
    () => () => {
      isMounted.current = false;
    },
    [],
  );
  const [saveOffersFromListing, { isLoading: isSaving }] =
    mortgageMiniCalculatorApi.endpoints.saveOffersFromListing.useMutation();
  const [fetchActualApplication, { isFetching: isActualApplicationFetching }] =
    mortgageMiniCalculatorApi.endpoints.getActualApplication.useLazyQuery();

  /* Выдачу сохраняем ради applicationId и ссылки: и то, и другое формирует бэк.
     programsVersion тут всегда есть — без него useAuthAction не пускает в эту ветку */
  const saveOffersForAnketa = async (): Promise<string | null> => {
    try {
      const { redirectUrl } = await withRequestTimeout(saveOffersFromListing(saveOffersParams));

      dispatch(setShortFormRedirectUrl(redirectUrl));

      return redirectUrl;
    } catch {
      return null;
    }
  };

  const buildNewAnketaUrl = (redirectUrl: string | null) =>
    buildShortFormUrl({ redirectUrl, draftAction: 'new', bankId });

  /* Без ссылки из ответа собрать переход некуда: раньше был запасной адрес из РС, теперь его нет */
  const redirectToNewAnketaOrLogFailure = (redirectUrl: string | null) => {
    const newAnketaUrl = buildNewAnketaUrl(redirectUrl);

    if (!newAnketaUrl) {
      logger.error(new Error('Не удалось собрать ссылку на короткую анкету'), {
        domain: 'useShortFormAuthAction',
        description: 'В ответе save-offers-from-listing нет redirectUrl',
      });

      return;
    }

    redirect(newAnketaUrl);
  };

  const handleShortFormAuthAction = async () => {
    /* Трекинг до запросов: после await уход со страницы потеряет клик только в группе B */
    trackingFunction?.();

    if (!isCianAuthorized) {
      redirectToNewAnketaOrLogFailure(await saveOffersForAnketa());

      return;
    }

    // Сохранение не ждём: показывать модалку или нет, решает только ответ о текущей заявке
    const savingRedirectUrl = saveOffersForAnketa();
    const decision = await resolveActualApplication(fetchActualApplication());

    if (decision.kind === 'none') {
      redirectToNewAnketaOrLogFailure(await savingRedirectUrl);

      return;
    }

    if (decision.kind === 'submitted') {
      if (!isMounted.current) {
        return;
      }

      /* Модалка оффера, из которой мог прийти клик, лежит в общем ModalWindow — иначе модалка в модалке */
      dispatch(closeModal());
      dispatch(
        openSubmittedAnketaModal({
          offerForAnalytic: offerForAnalytic ?? null,
          listNumber: listNumber ?? null,
          application: decision.application,
        }),
      );

      return;
    }

    /* Ссылку ждём до открытия: обе кнопки модалки ведут по ней, и модалка без неё —
       выбор из двух мёртвых кнопок без права на повтор */
    const newAnketaUrl = buildNewAnketaUrl(await savingRedirectUrl);

    if (!isMounted.current) {
      return;
    }

    /* Показывать нечего, зато повторный клик по CTA перезапустит сохранение выдачи */
    if (!newAnketaUrl) {
      logger.error(new Error('Не удалось собрать ссылку на короткую анкету'), {
        domain: 'useShortFormAuthAction',
        description: 'Модалка черновика не показана: в ответе save-offers-from-listing нет redirectUrl',
      });

      return;
    }

    dispatch(closeModal());
    dispatch(
      openUnfinishedAnketaModal({
        offerForAnalytic: offerForAnalytic ?? null,
        listNumber: listNumber ?? null,
        newAnketaUrl,
      }),
    );
  };

  return { isLoading: isSaving || isActualApplicationFetching || isOffersFetching, handleShortFormAuthAction };
};
